import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Sparkles,
  Search,
  MessageSquare,
  Calendar,
  Home,
  Phone,
  ChevronDown,
  ChevronUp,
  Ban,
} from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import {
  type ClientRow,
  useClients,
  usePropertyMatches,
  useSearchCriteria,
  useSendClientSms,
} from '@/hooks/data/useClients';
import { SearchCriteriaDialog } from '@/components/clients/SearchCriteriaDialog';
import { CreateAppointmentDialog } from '@/components/appointments/CreateAppointmentDialog';
import { SendSmsDialog } from '@/components/clients/SendSmsDialog';
import { useConfirm } from '@/components/ui/confirm';
import { useSearchTracker } from '@/hooks/useSearchTracker';
import { formatMoney, formatNumber } from '@/lib/formatLocale';
import { humanizeApiError } from '@/lib/errorMessages';

export const Route = createFileRoute('/_app/interessenten')({
  component: InteressentenPage,
});

function InteressentenPage() {
  const { t } = useTranslation();
  const { data: buyers = [], isLoading } = useClients({ clientType: 'kupac' });
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return buyers;
    return buyers.filter((b) =>
      [b.first_name, b.last_name, b.phone, b.location].filter(Boolean).join(' ').toLowerCase().includes(s),
    );
  }, [buyers, search]);

  // Batch ZR (Task 3) — search history
  useSearchTracker('interessenten', search, null, filtered.length);

  const stats = useMemo(() => {
    const wantsViewing = buyers.filter((b) => b.interest_status === 'wants_viewing').length;
    const offerMade = buyers.filter((b) => b.interest_status === 'offer_made').length;
    return { total: buyers.length, wantsViewing, offerMade };
  }, [buyers]);

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink">
          {t('interessenten.title', 'Interessenten')}
        </h2>
        <p className="text-sm text-ink-3">
          {t(
            'interessenten.subtitle',
            'Käufer mit Suchprofil — automatisches Matching mit aktiven Immobilien.',
          )}
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('interessenten.stats.total', 'Gesamt')}</p>
          <p className="fm-kpi-value">{formatNumber(stats.total)}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('interessenten.stats.wants_viewing', 'Will Besichtigung')}</p>
          <p className="fm-kpi-value">{stats.wantsViewing}</p>
        </div>
        <div className="fm-kpi !border-ink !bg-ink">
          <p className="fm-kpi-label !text-gold">
            {t('interessenten.stats.offer_made', 'Anbot abgegeben')}
          </p>
          <p className="fm-kpi-value !text-paper">{stats.offerMade}</p>
        </div>
      </div>

      <div className="fm-card p-5 space-y-4">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('interessenten.search', 'Suchen nach Name, Telefon, Ort …')}
            className="w-full rounded-md border border-line bg-card ps-9 pe-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          />
        </div>

        {isLoading ? (
          <p className="py-12 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-3">
            {t('interessenten.empty', 'Keine Interessenten gefunden.')}
          </p>
        ) : (
          <ul className="space-y-3">
            {filtered.map((b) => (
              <InteressentRow key={b.id} client={b} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const INTEREST_PILL: Record<ClientRow['interest_status'], string> = {
  none: 'fm-pill-neutral',
  wants_viewing: 'fm-pill-info',
  viewing_scheduled: 'fm-pill-warn',
  offer_made: 'fm-pill-gold',
  closed: 'fm-pill-pos',
};

function InteressentRow({ client }: { client: ClientRow }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [expanded, setExpanded] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [smsing, setSmsing] = useState(false);
  const { data: criteria } = useSearchCriteria(expanded ? client.id : undefined);
  const { data: matches = [], isFetching } = usePropertyMatches(expanded ? client.id : undefined);
  const sendSms = useSendClientSms();

  const fullName = [client.title, client.first_name, client.last_name].filter(Boolean).join(' ');
  // DSGVO objection — the server refuses the send (409 client_opted_out).
  const optedOut = client.do_not_call === true;

  const onBulkSms = async () => {
    if (matches.length === 0 || optedOut) return;
    const ok = await confirm({
      title: t('interessenten.bulk_sms.title', 'SMS-Übersicht senden?'),
      body: `${matches.length} ${t('interessenten.bulk_sms.matches', 'passende Immobilien')} → ${fullName}`,
      confirmLabel: t('common.send', 'Senden'),
    });
    if (!ok) return;
    const lines = matches.slice(0, 5).map((m) => {
      const price = m.purchasePrice
        ? formatMoney(Number(m.purchasePrice), { maximumFractionDigits: 0 })
        : '';
      return `• ${m.address ?? m.city}${price ? ` — ${price}` : ''}`;
    });
    const message = `4 M Immobilien — passende Immobilien für Sie:\n${lines.join('\n')}\n\nMehr Details bei Ihrem Makler.`;
    try {
      await sendSms.mutateAsync({ id: client.id, message });
      toast.success(t('interessenten.bulk_sms.sent', 'SMS gesendet.'));
    } catch (cause) {
      toast.error(humanizeApiError((cause as Error).message, t, t('clients.sms.failed', 'Senden fehlgeschlagen')));
    }
  };

  return (
    <li className="rounded-xl border border-line bg-card overflow-hidden">
      {/* Mobile: 2-row layout (name+pill / phone+actions) — desktop unchanged */}
      <div className="flex flex-col gap-2.5 p-4 md:flex-row md:items-center md:gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={fullName} size="md" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-ink truncate">{fullName}</p>
            <p className="inline-flex items-center gap-2 text-xs text-ink-3">
              <Phone className="h-3 w-3" />
              <span className="font-mono">{client.phone}</span>
              {client.location && <span>· {client.location}</span>}
            </p>
            {optedOut && (
              <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--danger))]">
                <Ban className="h-3 w-3" />
                {t('clients.do_not_call.badge', 'Werbung untersagt (DSGVO)')}
              </p>
            )}
          </div>
          <span className={`fm-pill ms-auto md:ms-0 ${INTEREST_PILL[client.interest_status]}`}>
            {t(`enums.interest_status.${client.interest_status}`, client.interest_status)}
          </span>
        </div>
        <div className="flex items-center justify-end gap-1 md:ms-auto">
          <button
            type="button"
            onClick={() => setEditingCriteria(true)}
            title={t('interessenten.edit_criteria', 'Suchkriterien bearbeiten')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
          >
            <Search className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setSmsing(true)}
            title={t('interessenten.send_sms', 'SMS senden')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
          >
            <MessageSquare className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setScheduling(true)}
            title={t('interessenten.schedule', 'Besichtigung vereinbaren')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
          >
            <Calendar className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ms-1 inline-flex items-center gap-1 rounded-md bg-ink px-3 py-2 text-xs font-semibold text-paper hover:bg-ink/90"
          >
            <Sparkles className="h-3 w-3" />
            {t('interessenten.matches', 'Matches')}
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-line bg-paper-2 p-4 space-y-3">
          {criteria ? (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="font-bold text-ink-3 uppercase tracking-[0.08em]">Suchprofil:</span>
              {criteria.min_price && (
                <Tag>
                  ab € {formatNumber((criteria.min_price / 100), { maximumFractionDigits: 0 })}
                </Tag>
              )}
              {criteria.max_price && (
                <Tag>
                  bis € {formatNumber((criteria.max_price / 100), { maximumFractionDigits: 0 })}
                </Tag>
              )}
              {criteria.min_size && <Tag>min {criteria.min_size} m²</Tag>}
              {criteria.max_size && <Tag>max {criteria.max_size} m²</Tag>}
              {criteria.rooms_min && <Tag>{criteria.rooms_min}+ Zi</Tag>}
              {criteria.districts?.map((d) => (
                <Tag key={d}>{d}</Tag>
              ))}
              {criteria.building_types?.map((b) => (
                <Tag key={b}>{b}</Tag>
              ))}
            </div>
          ) : (
            <p className="text-xs text-ink-3">
              {t(
                'interessenten.no_criteria',
                'Noch keine Suchkriterien. Klicken Sie auf das Lupe-Symbol, um sie zu definieren.',
              )}
            </p>
          )}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
                {t('interessenten.match_list', 'Passende Immobilien')}{' '}
                {!isFetching && `(${matches.length})`}
              </h4>
              {matches.length > 0 && (
                <button
                  type="button"
                  onClick={onBulkSms}
                  disabled={sendSms.isPending || optedOut}
                  title={optedOut ? t('clients.do_not_call.badge', 'Werbung untersagt (DSGVO)') : undefined}
                  className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-2.5 py-1 text-[11px] font-medium hover:bg-paper-3 disabled:opacity-50"
                >
                  <MessageSquare className="h-3 w-3" />
                  {t('interessenten.bulk_sms.cta', 'Übersicht per SMS')}
                </button>
              )}
            </div>
            {isFetching ? (
              <p className="py-3 text-center text-xs text-ink-3">{t('common.loading', 'Lädt …')}</p>
            ) : matches.length === 0 ? (
              <p className="py-3 text-center text-xs text-ink-3">
                {t(
                  'interessenten.match_empty',
                  'Aktuell keine passenden Objekte im aktiven Bestand.',
                )}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {matches.map((m) => (
                  <li
                    key={m.propertyId}
                    className="flex items-center gap-3 rounded-md border border-line bg-card px-3 py-2"
                  >
                    <Home className="h-4 w-4 flex-shrink-0 text-ink-3" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink truncate">
                        {m.address ?? m.city}
                      </p>
                      <p className="text-[11px] text-ink-3 truncate">
                        {[
                          m.postalCode,
                          m.district,
                          m.rooms && `${m.rooms} Zi`,
                          m.size && `${m.size} m²`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    {m.purchasePrice && (
                      <span className="font-medium text-sm text-ink">
                        € {formatNumber(Number(m.purchasePrice), { maximumFractionDigits: 0 })}
                      </span>
                    )}
                    <span className="fm-pill fm-pill-gold" title={m.matched.join(', ')}>
                      Score {m.score}
                    </span>
                    <Link
                      to="/properties"
                      className="text-xs text-ink-3 underline-offset-4 hover:underline"
                    >
                      Öffnen
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {editingCriteria && (
        <SearchCriteriaDialog
          client={client}
          open={true}
          onOpenChange={(o) => !o && setEditingCriteria(false)}
        />
      )}
      {scheduling && (
        <CreateAppointmentDialog
          open={true}
          onOpenChange={(o) => !o && setScheduling(false)}
          defaultClientId={client.id}
          defaultType="besichtigung"
        />
      )}
      {smsing && (
        <SendSmsDialog
          client={client}
          open={true}
          onOpenChange={(o) => !o && setSmsing(false)}
        />
      )}
    </li>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-md border border-line bg-card px-2 py-0.5 text-xs text-ink-2">
      {children}
    </span>
  );
}
